import { useMemo, ReactNode } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { RadioButton } from '@components/RadioButton';
import { CaretIcon } from '@icons/CaretIcon';
import { useEpisodeDetailsContext } from '@insights_module/context/EpisodeDetailsContext';
import {
  LinkedNamedEntityForEpisodeItem,
  LinkedNamedEntityReference,
} from '@insights_module/types/ContextualAndEntity';

const Container = tw.div`bg-page-background py-[6px] pl-[8px] pr-[10px] [&:not(:last-child)]:mb-[4px] rounded-[4px]`;
const EntityToggler = tw.button`w-full`;
const GridRow = tw.div`grid grid-cols-[2fr,60px,1fr] items-center`;
const GridCell = tw.div`w-full h-full flex items-center justify-end font-semibold text-text-and-icon-muted-2 text-[12px]`;
const LinkedEnitites = tw.div`max-h-0 overflow-hidden transition-max-height duration-200`;
const IconWrapper = tw.span`rounded-[3px] transition-all duration-200 h-[22px] w-[22px] inline-flex items-center justify-center`;
const Caret = tw(CaretIcon)`fill-text-and-icon rotate-[-90deg] transition-all duration-200`;
const TypeLabelContainer = tw.div`block h-[22px] bg-sec-1 rounded-[3px] px-[4px] flex flex-row items-center justify-center`;
const TypeLabelText = tw.span`inline-block uppercase text-accent-1 text-[12px] font-normal leading-[130%] text-center`;
const BaseText = tw.span`block text-[13px] text-text-and-icon text-left font-normal tracking-[0.13px] transition-all duration-200`;
const Subtitle = tw(BaseText)`text-[12px] font-semibold text-text-and-icon-muted mb-[6px] mt-[8px]`;
const RadioLabel = tw(BaseText)`text-[14px] whitespace-nowrap`;
const LinkedEntitesContainer = tw.div`flex flex-wrap gap-[6px]`;

type RadioInputPropsType = {
  children: ReactNode;
  selected: boolean;
  onChange: () => void;
};

const RadioInput = ({ children, selected, onChange }: RadioInputPropsType) => {
  return (
    <RadioButton
      selected={selected}
      onChange={onChange}
      tw='rounded-full py-[2px] px-[8px] bg-gray-3'>
      <RadioLabel tw='ml-1'>{children}</RadioLabel>
    </RadioButton>
  );
};

const expandedStyle = {
  container: tw`bg-gray-2!`,
  toggler: tw`bg-sec-1!`,
  icon: tw`rotate-0!`,
  linkedEntities: tw`max-h-[500px]!`,
  text: tw`text-text-and-icon-muted!`,
  title: tw`text-accent-1`,
};

export const EpisodeNamedEntity = ({
  entity,
  ...rest
}: {
  entity: LinkedNamedEntityForEpisodeItem;
}) => {
  const { t } = useTranslation();
  const { setIsAudioPlaying, selectedEntity, setSelectedEntity } = useEpisodeDetailsContext();

  const isSelected = useMemo((): boolean => {
    if (!selectedEntity) {
      return false;
    }

    const entityReferences = entity.references.map((ref) => ref.value);

    return entityReferences.some((ref) => ref === selectedEntity.value);
  }, [entity, selectedEntity]);

  const toggleExpand = (entity: LinkedNamedEntityReference) => {
    if (isSelected) {
      setSelectedEntity(null);

      return;
    }
    setIsAudioPlaying(false);
    setSelectedEntity(entity as LinkedNamedEntityReference);
  };

  return (
    <Container css={[isSelected && expandedStyle.container]} {...rest}>
      <EntityToggler onClick={() => toggleExpand(entity.references[0])}>
        <GridRow>
          <GridCell tw='justify-start cursor-pointer'>
            <BaseText css={[isSelected && expandedStyle.title]}>
              {entity.value} ({entity.referencesCount})
            </BaseText>
          </GridCell>
          <GridCell>
            <TypeLabelContainer>
              <TypeLabelText>{entity?.type}</TypeLabelText>
            </TypeLabelContainer>
          </GridCell>
          <GridCell>
            <IconWrapper css={[isSelected && expandedStyle.toggler]}>
              <Caret css={[isSelected && expandedStyle.icon]} />
            </IconWrapper>
          </GridCell>
        </GridRow>
      </EntityToggler>
      <LinkedEnitites css={[isSelected && expandedStyle.linkedEntities]}>
        {isSelected && (
          <>
            <Subtitle>
              {t('insights.episode_details_drawer.episode_details_sidebar.entities.table.subtitle')}
            </Subtitle>
            <LinkedEntitesContainer>
              {entity.references.map((item) => (
                <RadioInput
                  key={item.value}
                  selected={selectedEntity?.value === item.value}
                  onChange={() => setSelectedEntity(item as LinkedNamedEntityReference)}>
                  {item.value} ({item.count})
                </RadioInput>
              ))}
            </LinkedEntitesContainer>
          </>
        )}
      </LinkedEnitites>
    </Container>
  );
};
